/**
 * Per-note scoring of a DTW `Alignment` against its target `NotationTimeline`
 * (Phase 5, composition mode). Pure: reuses `gradePitch` (pitch banding) and the
 * held-flat/wavered gamaka thresholds from `analysis.ts` — no grading logic is
 * reimplemented here.
 */
import { HELD_FLAT_CENTS, WAVER_CENTS, isOscillatingGamaka } from '../analysis.js';
import { gradePitch } from '../evaluate.js';
const fold = (c) => ((c % 1200) + 1200) % 1200;
const centsToHz = (cents, saHz) => saHz * 2 ** (cents / 1200);
function median(xs) {
    if (xs.length === 0)
        return 0;
    const sorted = [...xs].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
}
/** Peak-to-peak cents extent of a note's sung span, unwrapped relative to its own
 * first frame (same trick as analysis.ts) so a run straddling the Sa/octave seam
 * doesn't read as a bogus ~1200¢ swing. Shift-invariant, so the reference frame
 * choice doesn't affect the result. */
function gamakaExtent(cents) {
    const ref = fold(cents[0]);
    const unwrapped = cents.map((c) => {
        let d = fold(c) - ref;
        if (d > 600)
            d -= 1200;
        else if (d < -600)
            d += 1200;
        return d;
    });
    return Math.max(...unwrapped) - Math.min(...unwrapped);
}
const BAND_SEVERITY = { excellent: 0, good: 1, off: 2, wrong: 3 };
/**
 * Score a DTW alignment note-by-note against its target timeline.
 *
 * `sung` (the full pitch trace) is accepted for interface symmetry with
 * `dtwAlign` — every frame this function needs is already carried per-note on
 * `alignment.notes[i].sungCents`, so it isn't re-consulted here.
 */
export function scoreAlignment(alignment, target, sung, saHz, level, gamakaProfile) {
    void sung;
    const beatMs = 60000 / target.tempoBpm;
    const notes = target.notes.map((note, idx) => {
        const aligned = alignment.notes[idx];
        const sungCents = aligned?.sungCents ?? [];
        if (sungCents.length === 0) {
            return {
                noteIndex: note.index,
                swara: note.swara,
                band: 'wrong',
                centsOffset: 0,
                timingOffsetMs: 0,
                missed: true,
            };
        }
        const foldedCents = sungCents.map((f) => fold(f.cents));
        const medianCents = median(foldedCents);
        const grade = gradePitch(centsToHz(medianCents, saHz), saHz, { [note.swara]: note.targetCents }, level);
        // Only one candidate swara is passed to gradePitch above (the note's own
        // target), so its "nearest" is always this note — centsOffset is exactly
        // that same signed, wrapped distance. Reuse it instead of re-deriving it,
        // so there's only one wrap-boundary convention for "signed cents from target".
        const centsOffset = grade.centsFromNearest;
        const expectedMs = note.startBeat * beatMs;
        const timingOffsetMs = aligned.sungStartMs - expectedMs;
        // Mirrors analysis.ts's gamaka verdict exactly (same constants, same two
        // branches): held-flat only applies to a swara that's *supposed* to move
        // (oscillating profile) but didn't; wavered only applies to a swara that's
        // supposed to stay put (steady profile) but wobbled. A profile-less swara
        // has no expectation to check against, so it gets no verdict at all.
        const profile = gamakaProfile[note.swara];
        let gamakaVerdict;
        if (profile) {
            const oscillating = isOscillatingGamaka(profile);
            const extent = gamakaExtent(sungCents.map((f) => f.cents));
            gamakaVerdict = oscillating
                ? extent < HELD_FLAT_CENTS
                    ? 'held-flat'
                    : 'ok'
                : extent > WAVER_CENTS
                    ? 'wavered'
                    : 'ok';
        }
        return {
            noteIndex: note.index,
            swara: note.swara,
            band: grade.band,
            centsOffset,
            timingOffsetMs,
            missed: false,
            ...(gamakaVerdict !== undefined ? { gamakaVerdict } : {}),
        };
    });
    const nonMissed = notes.filter((n) => !n.missed);
    const onPitch = nonMissed.filter((n) => n.band === 'excellent' || n.band === 'good');
    const accuracyPct = notes.length ? (100 * onPitch.length) / notes.length : 0;
    const meanTimingMs = nonMissed.length
        ? nonMissed.reduce((a, n) => a + n.timingOffsetMs, 0) / nonMissed.length
        : 0;
    const worst = notes
        .filter((n) => n.missed || n.band === 'off' || n.band === 'wrong')
        .sort((a, b) => {
        if (a.missed !== b.missed)
            return a.missed ? -1 : 1;
        const sev = BAND_SEVERITY[b.band] - BAND_SEVERITY[a.band];
        return sev !== 0 ? sev : Math.abs(b.centsOffset) - Math.abs(a.centsOffset);
    })
        .map((n) => n.noteIndex);
    return { notes, summary: { accuracyPct, meanTimingMs, worst } };
}
//# sourceMappingURL=score.js.map